/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {checkFirstAccessEmail, login, setFirstAccessPassword} from '../core/_requests'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'

const emailSchema = Yup.string()
  .email('Formato errado de email')
  .min(6, 'Tamanho mínimo de 6 characteres')
  .max(50, 'Tamanho máximo de 50 caracteres')
  .required('Email é obrigatório')

const loginSchema = Yup.object().shape({
  email: emailSchema,
  password: Yup.string()
    .min(6, 'Tamanho mínimo de 6 characteres')
    .max(50, 'Tamanho máximo de 50 caracteres')
    .required('Senha é obrigatória'),
})

const firstAccessEmailSchema = Yup.object().shape({
  email: emailSchema,
})

const firstAccessPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'A senha deve ter no mínimo 6 caracteres')
    .max(50, 'Tamanho máximo de 50 caracteres')
    .required('Senha é obrigatória'),
  passwordConfirmation: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'As senhas devem coincidir')
    .required('Confirmação de senha é obrigatória'),
})

const initialValues = {
  email: '',
  password: '',
}

type LoginMode = 'login' | 'firstAccessEmail' | 'firstAccessPassword'

export function Login() {
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<LoginMode>('login')
  const [firstAccessEmail, setFirstAccessEmail] = useState('')
  const [failedLogin, setFailedLogin] = useState(false)
  const [firstAccessError, setFirstAccessError] = useState('')
  const {saveAuth, setCurrentUser} = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setFailedLogin(false)
      try {
        const response = await login(values.email, values.password)
        const {token, user} = response.data.data
        saveAuth(token)
        setCurrentUser(user)
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setFailedLogin(true)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const firstAccessEmailFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: firstAccessEmailSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setLoading(true)
      setFirstAccessError('')
      try {
        await checkFirstAccessEmail(values.email)
        setFirstAccessEmail(values.email)
        setMode('firstAccessPassword')
      } catch (error: any) {
        console.error(error)
        setFirstAccessError(
          error?.response?.data?.data?.email ||
            error?.response?.data?.message ||
            'Não foi possível iniciar o primeiro acesso'
        )
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const firstAccessPasswordFormik = useFormik({
    initialValues: {
      password: '',
      passwordConfirmation: '',
    },
    validationSchema: firstAccessPasswordSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setLoading(true)
      setFirstAccessError('')
      try {
        const response = await setFirstAccessPassword(
          firstAccessEmail,
          values.password,
          values.passwordConfirmation
        )
        const {token, user} = response.data.data
        saveAuth(token)
        setCurrentUser(user)
      } catch (error: any) {
        console.error(error)
        setFirstAccessError(
          error?.response?.data?.data?.email ||
            error?.response?.data?.message ||
            'Não foi possível definir a senha'
        )
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const renderLogo = () => (
    <div className='text-center mb-11'>
      <img
        alt='Logo'
        src={toAbsoluteUrl('/media/logos/logo_colorido_fundo_transparente.png')}
        className='h-85px app-sidebar-logo-default'
      />
    </div>
  )

  if (mode === 'firstAccessEmail') {
    return (
      <form
        className='form w-100'
        onSubmit={firstAccessEmailFormik.handleSubmit}
        noValidate
        id='kt_first_access_email_form'
      >
        {renderLogo()}

        <div className='text-center mb-8'>
          <h1 className='text-dark fw-bolder mb-3'>Primeiro acesso</h1>
        </div>

        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
          <input
            placeholder='Email'
            {...firstAccessEmailFormik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid':
                  firstAccessEmailFormik.touched.email && firstAccessEmailFormik.errors.email,
              },
              {
                'is-valid':
                  firstAccessEmailFormik.touched.email && !firstAccessEmailFormik.errors.email,
              }
            )}
            type='email'
            name='email'
            autoComplete='off'
          />
          {firstAccessEmailFormik.touched.email && firstAccessEmailFormik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{firstAccessEmailFormik.errors.email}</span>
              </div>
            </div>
          )}
        </div>

        {firstAccessError && <span className='text-danger d-block mb-5'>{firstAccessError}</span>}

        <div className='d-grid mb-5'>
          <button
            type='submit'
            className='btn'
            style={{backgroundColor: '#379479', color: '#fff'}}
            disabled={firstAccessEmailFormik.isSubmitting || !firstAccessEmailFormik.isValid}
          >
            {!loading && <span className='indicator-label'>Continuar</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Por favor, espere
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>

        <div className='text-center'>
          <a
            className='link-primary fw-semibold'
            onClick={() => {
              setFirstAccessError('')
              setMode('login')
            }}
          >
            Voltar para o login
          </a>
        </div>
      </form>
    )
  }

  if (mode === 'firstAccessPassword') {
    return (
      <form
        className='form w-100'
        onSubmit={firstAccessPasswordFormik.handleSubmit}
        noValidate
        id='kt_first_access_password_form'
      >
        {renderLogo()}

        <div className='text-center mb-8'>
          <h1 className='text-dark fw-bolder mb-3'>Definir senha</h1>
          <div className='text-gray-600 fw-semibold'>{firstAccessEmail}</div>
        </div>

        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder text-dark'>Nova senha</label>
          <input
            type='password'
            autoComplete='new-password'
            {...firstAccessPasswordFormik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid':
                  firstAccessPasswordFormik.touched.password &&
                  firstAccessPasswordFormik.errors.password,
              },
              {
                'is-valid':
                  firstAccessPasswordFormik.touched.password &&
                  !firstAccessPasswordFormik.errors.password,
              }
            )}
          />
          {firstAccessPasswordFormik.touched.password &&
            firstAccessPasswordFormik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{firstAccessPasswordFormik.errors.password}</span>
                </div>
              </div>
            )}
        </div>

        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder text-dark'>Confirmar senha</label>
          <input
            type='password'
            autoComplete='new-password'
            {...firstAccessPasswordFormik.getFieldProps('passwordConfirmation')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid':
                  firstAccessPasswordFormik.touched.passwordConfirmation &&
                  firstAccessPasswordFormik.errors.passwordConfirmation,
              },
              {
                'is-valid':
                  firstAccessPasswordFormik.touched.passwordConfirmation &&
                  !firstAccessPasswordFormik.errors.passwordConfirmation,
              }
            )}
          />
          {firstAccessPasswordFormik.touched.passwordConfirmation &&
            firstAccessPasswordFormik.errors.passwordConfirmation && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>
                    {firstAccessPasswordFormik.errors.passwordConfirmation}
                  </span>
                </div>
              </div>
            )}
        </div>

        {firstAccessError && <span className='text-danger d-block mb-5'>{firstAccessError}</span>}

        <div className='d-grid mb-5'>
          <button
            type='submit'
            className='btn'
            style={{backgroundColor: '#379479', color: '#fff'}}
            disabled={
              firstAccessPasswordFormik.isSubmitting || !firstAccessPasswordFormik.isValid
            }
          >
            {!loading && <span className='indicator-label'>Salvar senha</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Por favor, espere
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>

        <div className='text-center'>
          <a
            className='link-primary fw-semibold'
            onClick={() => {
              setFirstAccessError('')
              setFirstAccessEmail('')
              firstAccessPasswordFormik.resetForm()
              setMode('firstAccessEmail')
            }}
          >
            Usar outro email
          </a>
        </div>
      </form>
    )
  }

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {renderLogo()}

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Senha</label>
        <input
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />
        <a
          className='link-primary'
          onClick={() => {
            setFailedLogin(false)
            setFirstAccessError('')
            setMode('firstAccessEmail')
          }}
        >
          Primeiro acesso
        </a>
      </div>

      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn'
          style={{backgroundColor: '#379479', color: '#fff'}}
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Entrar</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Por favor, espere
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        {failedLogin && (
          <span style={{color: '#FF3333', marginTop: `10px`}}>
            Erro: Usuario ou senha incorreto
          </span>
        )}
      </div>
    </form>
  )
}
